// src/components/VideoPlayer.jsx
import React, { useEffect, useRef, forwardRef, useState } from "react";
import Hls from "hls.js";

const HLS_CONFIG = {
  lowLatencyMode: true,
  liveSyncDuration: 2.5,
  liveMaxLatencyDuration: 5.0,
  startPosition: -1,
  backBufferLength: 30,
  enableWorker: true,
};

// Onthoud of deze gebruiker audio al eens heeft geactiveerd op dit origin
const AUDIO_FLAG_KEY = "bond:audioAllowed";
const getAudioAllowed = () => {
  try {
    return localStorage.getItem(AUDIO_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};
const setAudioAllowed = (v) => {
  try {
    localStorage.setItem(AUDIO_FLAG_KEY, v ? "1" : "0");
  } catch {}
};

const VideoPlayer = forwardRef(function VideoPlayer(
  {
    source,
    serverOffsetMs = 0,
    profile,
    mediaStateRef,
    needInitialAlignRef,
    didInitialAlignRef,
    lastFragBoostUntilRef,
    hardSeeksRef,
    style,
    onDoubleClick,
    controlsList = "nodownload noplaybackrate noremoteplayback",
    playsInline = true,
    preload = "auto",
    objectFit = "contain",
  },
  videoRef
) {
  const hlsInstRef = useRef(null);

  // deze states gebruiken we nog intern voor autoplay-policy, maar we tonen géén eigen UI meer
  const [muted, setMuted] = useState(true); // autoplay policy: start muted
  const [volume] = useState(1); // 0..1
  const [uiVisible, setUiVisible] = useState(true); // heeft nu geen visueel effect meer
  const audioAllowedRef = useRef(getAudioAllowed());

  // HLS attach + initial align
  useEffect(() => {
    const v = videoRef?.current;
    if (!v || !source) return;

    // teardown
    if (hlsInstRef.current) {
      try {
        hlsInstRef.current.destroy();
      } catch {}
      hlsInstRef.current = null;
    }

    // basis video props
    v.playsInline = playsInline;
    v.preload = preload;
    v.volume = volume;
    v.muted = true; // BELANGRIJK: autoplay toestaan

    if (source.mode === "hls") {
      if (Hls.isSupported()) {
        const hls = new Hls(HLS_CONFIG);
        hlsInstRef.current = hls;
        hls.attachMedia(v);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(source.url);
        });

        const tryInitialSnap = () => {
          const s = mediaStateRef?.current;
          const now = Date.now() + serverOffsetMs;
          if (s) {
            const elapsed = Math.max(0, (now - s.serverEpoch) / 1000);
            const target = s.playing ? s.mediaTime + elapsed : s.mediaTime;
            try {
              v.fastSeek ? v.fastSeek(target) : (v.currentTime = target);
            } catch {}
          } else {
            try {
              hls.startLoad(-1);
            } catch {}
          }
          // probeer meteen te spelen (muted → toegestaan)
          v.play().catch(() => {});
        };

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (hls.audioTracks && hls.audioTracks.length > 0) {
            hls.audioTrack = 0; // kies eerste track
          }
          // initial align
          if (needInitialAlignRef?.current) {
            tryInitialSnap();
            needInitialAlignRef.current = false;
            didInitialAlignRef && (didInitialAlignRef.current = true);
          }
        });

        hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
          if (
            data?.details?.live &&
            lastFragBoostUntilRef &&
            profile?.BOOST_WINDOW_MS
          ) {
            lastFragBoostUntilRef.current =
              Date.now() + profile.BOOST_WINDOW_MS;
          }
        });

        hls.on(Hls.Events.FRAG_LOADED, () => {
          if (lastFragBoostUntilRef && profile?.BOOST_WINDOW_MS) {
            lastFragBoostUntilRef.current =
              Date.now() + profile.BOOST_WINDOW_MS;
          }
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (
            data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR ||
            data?.fatal
          ) {
            const s = mediaStateRef?.current;
            if (!s) return;
            const serverNow = Date.now() + serverOffsetMs;
            const elapsed = Math.max(0, (serverNow - s.serverEpoch) / 1000);
            const target = s.mediaTime + elapsed;
            try {
              v.fastSeek ? v.fastSeek(target) : (v.currentTime = target);
            } catch {}
            v.play().catch(() => {});
            hardSeeksRef &&
              (hardSeeksRef.current = (hardSeeksRef.current || 0) + 1);
            if (lastFragBoostUntilRef && profile?.BOOST_WINDOW_MS) {
              lastFragBoostUntilRef.current =
                Date.now() + profile.BOOST_WINDOW_MS;
            }
          }
        });
      } else {
        // Safari native HLS
        v.src = source.url || "";
        v.play().catch(() => {}); // muted autoplay
      }
    } else {
      // MP4
      v.src = source.url || "";
      v.play().catch(() => {}); // muted autoplay
    }

    const onCanPlay = () => {
      // fallback align voor MP4/native HLS
      if (needInitialAlignRef?.current) {
        const s = mediaStateRef?.current;
        const now = Date.now() + serverOffsetMs;
        if (s) {
          const elapsed = Math.max(0, (now - s.serverEpoch) / 1000);
          const target = s.playing ? s.mediaTime + elapsed : s.mediaTime;
          try {
            v.fastSeek ? v.fastSeek(target) : (v.currentTime = target);
          } catch {}
        }
        needInitialAlignRef.current = false;
        didInitialAlignRef && (didInitialAlignRef.current = true);
      }

      // Als user dit origin eerder toestemming gaf, zet meteen unmute
      if (audioAllowedRef.current) {
        v.muted = false;
        setMuted(false);
      }
    };

    v.addEventListener("canplay", onCanPlay);
    return () => {
      v.removeEventListener("canplay", onCanPlay);
      if (hlsInstRef.current) {
        try {
          hlsInstRef.current.destroy();
        } catch {}
        hlsInstRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.mode, source?.url, serverOffsetMs, profile?.BOOST_WINDOW_MS]);

  // kleine auto-hide state blijft bestaan, maar we tonen geen extra UI meer
  useEffect(() => {
    if (!uiVisible) return;
    const id = setTimeout(() => setUiVisible(false), 2500);
    return () => clearTimeout(id);
  }, [uiVisible]);

  return (
    <div
      onMouseMove={() => setUiVisible(true)}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <video
        ref={videoRef}
        onDoubleClick={onDoubleClick}
        controls={false}
        controlsList={controlsList}
        style={{
          width: "100%",
          height: "100%",
          objectFit,
          background: "#000",
          ...(style || {}),
        }}
      />
      {/* Geen eigen volume/mute UI meer hier:
          de audiomixer zit nu in App.js linksonder in de video-area */}
    </div>
  );
});

export default VideoPlayer;
